import type { SignatureContent } from '../../../../../../common/models/signature-tool/signature-tool.js'
import { formatPhoneDisplay, withCountryCode } from '../../../../../../common/utils/format-utils.js'
import type { EmployeeProfileData } from '../employee-profile-types.js'

/** Brand flags a variant overlays onto the shared base signature content. */
type SignatureBrand = Pick<
    SignatureContent,
    'isPlay' | 'isNope' | 'playLogo' | 'nopeLogo' | 'allianceLogo'
>

export interface SignatureBrandVariant {
    id: 'nope' | 'play'
    /** Brand display name — intentionally not translated. */
    label: string
    brand: SignatureBrand
}

/**
 * The two brands every person can copy: NOPE and gotoAndPlay (play). Each preset
 * toggles the matching company logo + link colour the `Signature` renderer
 * already knows about (play green / nope purple), and stacks the joint Alliance
 * logo directly beneath it (the renderer draws play/nope first, then alliance).
 */
export const SIGNATURE_BRAND_VARIANTS: readonly SignatureBrandVariant[] = [
    {
        id: 'nope',
        label: 'NOPE',
        brand: { isNope: true, isPlay: false, nopeLogo: true, allianceLogo: true },
    },
    {
        id: 'play',
        label: 'gotoAndPlay',
        brand: { isPlay: true, isNope: false, playLogo: true, allianceLogo: true },
    },
]

/**
 * The brand variants this person may copy: only the companies they belong to
 * (variant ids match the portal company slugs — 'nope' / 'play'). People in
 * both organisations get both. An employee with no company rows (data gap)
 * keeps both rather than losing the feature entirely.
 */
export const visibleBrandVariants = (
    companies: EmployeeProfileData['companies'],
): readonly SignatureBrandVariant[] => {
    const slugs = new Set(companies.map((company) => company.slug))
    const visible = SIGNATURE_BRAND_VARIANTS.filter((variant) => slugs.has(variant.id))
    return visible.length > 0 ? visible : SIGNATURE_BRAND_VARIANTS
}

const fullName = (employee: EmployeeProfileData): string =>
    `${employee.first_name} ${employee.last_name}`.trim()

interface SignatureBaseOptions {
    includePhone: boolean
    /**
     * The circle-cropped, publicly-hosted avatar URL (from `/api/signature-avatar`),
     * or null/undefined to render no photo. Not derived from `employee.avatar_url`:
     * email clients can't honour CSS `border-radius`, so the photo must be a
     * pre-cropped PNG served from a public absolute URL.
     */
    avatarUrl?: string | null
}

/**
 * Pure mapper: build the brand-agnostic signature content from an employee's
 * canonical profile data. Identity is never re-entered — it comes straight from
 * the profile (name/title/work email). The work phone is added as a `tel:` custom
 * line so it inherits the brand link colour, only when present and requested. The
 * photo is the supplied circular `avatarUrl` (omitted when null). Brand logos/flags
 * are applied per-variant on top of this base.
 */
export const employeeToSignatureBase = (
    employee: EmployeeProfileData,
    { includePhone, avatarUrl }: SignatureBaseOptions,
): SignatureContent => {
    const employment = employee.employment
    // A locally-stored number gets +372 so the link dials outside Estonia too.
    const workPhone = employment?.work_phone?.trim() ? withCountryCode(employment.work_phone) : null

    // Display the phone grouped for readability (+372 XXXX XXXX); the tel:
    // href must stay unspaced or it breaks click-to-call.
    const customLines =
        includePhone && workPhone
            ? [
                  {
                      key: 'Tel',
                      value: formatPhoneDisplay(workPhone),
                      link: `tel:${workPhone.replace(/[\s-]/g, '')}`,
                  },
              ]
            : []

    return {
        name: fullName(employee),
        title: employment?.job_title ?? '',
        email: employment?.work_email ?? employee.personal_email ?? undefined,
        imageUrl: avatarUrl ?? undefined,
        customLines,
        isPlay: false,
        isNope: false,
    }
}

/**
 * Full signature content for the 2026 NOPE design (TASK-344), rendered by
 * `SignatureNope` instead of the shared `Signature`. Differences from the play
 * base: the phone row is labelled "Phone" and its tel: link renders in the text
 * colour (the design shows it unhighlighted), the company site is a fixed
 * "Web: nope.ee" row, and `avatarUrl` must be the circle + doodle composite
 * (`nopeUrl` from `/api/signature-avatar`), not the plain circle crop.
 */
export const employeeToNopeSignatureContent = (
    employee: EmployeeProfileData,
    { includePhone, avatarUrl }: SignatureBaseOptions,
): SignatureContent => {
    const employment = employee.employment
    const workPhone = employment?.work_phone?.trim() ? withCountryCode(employment.work_phone) : null

    // Display grouped for readability; the tel: href must stay unspaced or it
    // breaks click-to-call.
    const customLines =
        includePhone && workPhone
            ? [
                  {
                      key: 'Phone',
                      value: formatPhoneDisplay(workPhone),
                      link: `tel:${workPhone.replace(/[\s-]/g, '')}`,
                  },
              ]
            : []

    return {
        name: fullName(employee),
        title: employment?.job_title ?? '',
        email: employment?.work_email ?? employee.personal_email ?? undefined,
        website: 'nope.ee',
        imageUrl: avatarUrl ?? undefined,
        customLines,
        isPlay: false,
        isNope: true,
        nopeLogo: true,
        allianceLogo: true,
    }
}
